import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { Hit } from "../../../../data-model/algoliaSearchModel";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import { getBadgePropsForAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { MyPaOrdersTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { rotateLastSegmentFirst } from "../../../../utils/getFullNameOfTheProvider";
import { capitalizeString } from "../../../../utils/stringModifications";

export const MyPaOrdersTableColumns: TableHeader[] = [
  {
    label: "Patient Name",
    key: MyPaOrdersTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 12,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Creation",
    key: MyPaOrdersTableColumnKeys.CREATED_AT,
    order: 2,
    width: 11,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Plan Details",
    key: MyPaOrdersTableColumnKeys.PLAN_DETAILS,
    order: 3,
    width: 12,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Medication",
    key: MyPaOrdersTableColumnKeys.MEDICATION,
    order: 4,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Provider",
    key: MyPaOrdersTableColumnKeys.PROVIDER,
    order: 5,
    width: 13,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "DOS",
    key: MyPaOrdersTableColumnKeys.DATE_OF_SERVICE,
    order: 6,
    width: 8,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "BO Value",
    key: MyPaOrdersTableColumnKeys.BO_VALUE,
    order: 7,
    width: 10,
    type: TableCellType.BADGE,
  },
  {
    label: "Auth Status",
    key: MyPaOrdersTableColumnKeys.AUTH_VERIFICATION,
    order: 8,
    width: 10,
    type: TableCellType.BADGE,
  },
  {
    label: "Alerts",
    key: MyPaOrdersTableColumnKeys.ALERTS,
    order: 9,
    width: 13,
    type: TableCellType.TAG_WITH_ICON,
  },
  {
    label: "",
    key: MyPaOrdersTableColumnKeys.RPA_STATUS,
    order: 10,
    width: 4,
    type: TableCellType.RPA_STATUS,
  },
  {
    label: "",
    key: MyPaOrdersTableColumnKeys.RPA_STATUS_TWO,
    order: 11,
    width: 3,
    type: TableCellType.RPA_STATUS_TWO,
  },
];

export const generateMyMedicalPaOrdersTableDataFromAlgoliaRecords = (
  algoliaRecords: Hit[],
  authStatusOptions: AuthStatusOptionModel[],
): MyMedicalPaOrdersTableData[] => {
  return algoliaRecords.map((algoliaRecord) => {
    const authStatus = getBadgePropsForAuthStatus(
      authStatusOptions,
      algoliaRecord?.auth_status,
    );

    return {
      [MyPaOrdersTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(
            algoliaRecord?.first_name ?? "",
            "",
            algoliaRecord?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${algoliaRecord?.patient_id ?? ""}`,
      },
      [MyPaOrdersTableColumnKeys.DATE_OF_SERVICE]: moment(
        algoliaRecord?.date_of_service_iso,
      ).format("MM/DD/YYYY"),
      [MyPaOrdersTableColumnKeys.PLAN_DETAILS]: {
        mainText: algoliaRecord?.primary_payer_name ?? "",
        secondaryText: algoliaRecord?.primary_member_id ?? "",
      },
      [MyPaOrdersTableColumnKeys.MEDICATION]: algoliaRecord?.regimen_name ?? "",

      [MyPaOrdersTableColumnKeys.ROW_DATA]: JSON.stringify(algoliaRecord),
      [MyPaOrdersTableColumnKeys.ID]: algoliaRecord?.id ?? "",
      [MyPaOrdersTableColumnKeys.CREATED_AT]: moment(
        algoliaRecord?.created_at_iso,
      ).format("MM/DD/YYYY"),
      [MyPaOrdersTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
      },
      [MyPaOrdersTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        algoliaRecord?.practitioner_name ?? "",
      ),
      [MyPaOrdersTableColumnKeys.BO_VALUE]: {
        text: algoliaRecord?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [MyPaOrdersTableColumnKeys.ALERTS]: {
        alert_badges: algoliaRecord?.alert_badges ?? [],
        alerts: algoliaRecord?.alerts ?? [],
      },
      [MyPaOrdersTableColumnKeys.RPA_STATUS]: {
        status:
          algoliaRecord?.ev_write_back_status ??
          EVWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: algoliaRecord?.id ?? "",
        masterAuthStatus: algoliaRecord?.master_auth_status ?? "",
      },

      [MyPaOrdersTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          algoliaRecord?.document_upload_status ??
          DocWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: algoliaRecord?.id ?? "",
        masterAuthStatus: algoliaRecord?.master_auth_status ?? "",
      },
    };
  });
};
